import { clsx } from 'clsx'
import type { PlayerListItem } from '../types'
import { Flag } from '@/shared/components/ui/Flag'
import { PlayerStatsContent, getInitials } from './PlayerStatsContent'
import { InjuryBadge } from './InjuryBadge'
import styles from './PlayerStatsSheet.module.css'

// ── Props ──────────────────────────────────────────────────────────────────────

type PlayerStatsSheetProps = {
  player:        PlayerListItem
  onClose:       () => void
  // Action props — omitted in read-only mode (e.g. viewing another member's team).
  readOnly?:     boolean
  isInSquad?:    boolean
  isWindowOpen?: boolean
  onAdd?:        (player: PlayerListItem) => void
  onRemove?:     (playerId: string) => void
}

// ── Position badge color classes ───────────────────────────────────────────────

const POS_BADGE_CLASS: Record<string, string | undefined> = {
  GK:  styles.posGK,
  DEF: styles.posDEF,
  MID: styles.posMID,
  FWD: styles.posFWD,
}

// ── Component ──────────────────────────────────────────────────────────────────

export function PlayerStatsSheet({
  player,
  onClose,
  readOnly = false,
  isInSquad,
  isWindowOpen,
  onAdd,
  onRemove,
}: PlayerStatsSheetProps) {
  const initials  = getInitials(player.displayName)
  const posClass  = POS_BADGE_CLASS[player.position]

  function handleAdd() {
    onAdd?.(player)
    onClose()
  }

  function handleRemove() {
    onRemove?.(player.playerId)
    onClose()
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className={styles.backdrop}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Sheet panel */}
      <div
        className={styles.sheet}
        role="dialog"
        aria-modal="true"
        aria-label={`Detalhes de ${player.displayName}`}
      >
        {/* Handle bar */}
        <div className={styles.handle} aria-hidden="true">
          <div className={styles.handleBar} />
        </div>

        {/* Player header */}
        <div className={styles.header}>
          <div className={styles.playerRow}>
            {/* Avatar */}
            <div className={styles.avatar} aria-hidden="true">
              {initials}
            </div>

            {/* Info */}
            <div className={styles.playerInfo}>
              <div className={styles.playerNameRow}>
                <span className={styles.playerName}>{player.displayName}</span>
                <InjuryBadge status={player.injuryStatus} />
              </div>
              <div className={styles.playerMeta}>
                <span className={clsx(styles.posBadgeSm, posClass)}>
                  {player.position}
                </span>
                <span className={styles.metaSep}>·</span>
                <span className={styles.metaPrice}>${player.price.toFixed(1)}</span>
                <span className={styles.metaSep}>·</span>
                <Flag code={player.teamCode} title={player.teamName} size={16} />
                <span>{player.teamName}</span>
                {player.nextOpponent && (
                  <span className={styles.metaNext} title="Próximo adversário">
                    vs {player.nextOpponent}
                  </span>
                )}
              </div>
            </div>

            {/* Action button — hidden when viewing read-only (another member's team) */}
            {!readOnly && (
            <div className={styles.actions}>
              {isInSquad ? (
                <button
                  className={styles.remBtn}
                  onClick={handleRemove}
                  disabled={!isWindowOpen}
                  aria-label={`Remover ${player.displayName}`}
                >
                  Remover
                </button>
              ) : (
                <button
                  className={styles.addBtn}
                  onClick={handleAdd}
                  disabled={!isWindowOpen}
                  aria-label={`Adicionar ${player.displayName}`}
                >
                  Adicionar
                </button>
              )}
            </div>
            )}
          </div>
        </div>

        {/* Stats content */}
        <PlayerStatsContent player={player} isMobile={true} />
      </div>
    </>
  )
}
